import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import './help.css';
import { helpContent, type HelpContent } from '../prototype/help/helpContent';
import { HelpStoreContext, type HelpStore } from './helpStore';
import { HelpOverlay } from './HelpOverlay';
import { createScriptedEngine, type ChatEngine } from './lib/chatEngine';
import { HELP_CONTACT_EMAIL, buildHelpMailto } from './lib/contactMailto';
import { flattenGroups, selectFaqGroups } from './lib/faqSelector';
import { resolveGuide } from './lib/guide';
import { HELP_CONTEXTS, HELP_FAQS } from './lib/helpData';
import { SHOW_DRAFT_FAQS } from './lib/helpFlags';
import {
  breadcrumbParent,
  contextChain,
  findContext,
  globalContext,
  resolveContext,
  resolveCtaTarget,
} from './lib/resolveContext';
import type { HelpContext, HelpFaq, HelpFaqCta, HelpMessage } from './lib/types';

export interface HelpProviderProps {
  children: ReactNode;
  contexts?: HelpContext[];
  faqs?: HelpFaq[];
  /** Draft FAQs render in dev or with VITE_HELP_SHOW_DRAFTS=true unless overridden. */
  includeDrafts?: boolean;
  /** Fixed typing dwell; by default scales with answer length within 300–600ms. */
  dwellMs?: number;
  engine?: ChatEngine;
  contactEmail?: string;
}

const DWELL_MIN_MS = 300;
const DWELL_MAX_MS = 600;

function dwellFor(text: string): number {
  return Math.min(DWELL_MAX_MS, Math.max(DWELL_MIN_MS, DWELL_MIN_MS + Math.round(text.length / 2)));
}

const POPOVER_BODIES = helpContent as Record<string, HelpContent | undefined>;
const popoverBodyFor = (helpKey: string) => POPOVER_BODIES[helpKey]?.body;

let messageCounter = 0;
function nextMessageId(): string {
  messageCounter += 1;
  return `help_msg_${Date.now()}_${messageCounter}`;
}

type MessageInput = Omit<HelpMessage, 'id' | 'createdAt'>;

export function HelpProvider({
  children,
  contexts = HELP_CONTEXTS,
  faqs = HELP_FAQS,
  includeDrafts = SHOW_DRAFT_FAQS,
  dwellMs,
  engine,
  contactEmail = HELP_CONTACT_EMAIL,
}: HelpProviderProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [activeKey, setActiveKey] = useState(() => globalContext(contexts).key);
  const [messages, setMessages] = useState<HelpMessage[]>([]);
  const [pending, setPending] = useState(false);
  const [selectedFaqId, setSelectedFaqId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  const isOpenRef = useRef(isOpen);
  const pendingRef = useRef(false);
  const activeKeyRef = useRef(activeKey);
  const replyTimer = useRef<number | null>(null);

  const chatEngine = useMemo(
    () => engine ?? createScriptedEngine({ faqs, contexts, includeDrafts }),
    [engine, faqs, contexts, includeDrafts],
  );
  const faqById = useMemo(() => new Map(faqs.map((faq) => [faq.id, faq])), [faqs]);

  const resolved = useMemo(() => resolveContext(location.pathname, contexts), [location.pathname, contexts]);
  const activeContext = useMemo(() => findContext(activeKey, contexts) ?? globalContext(contexts), [activeKey, contexts]);
  const parentContext = useMemo(() => breadcrumbParent(activeContext, contexts), [activeContext, contexts]);
  const faqGroups = useMemo(
    () => selectFaqGroups(activeContext.key, faqs, contexts, { includeDrafts }),
    [activeContext.key, faqs, contexts, includeDrafts],
  );
  const guide = useMemo(
    () => resolveGuide(contextChain(activeContext.key, contexts), flattenGroups(faqGroups)[0] ?? null, popoverBodyFor),
    [activeContext.key, contexts, faqGroups],
  );

  const setActive = useCallback((key: string) => {
    activeKeyRef.current = key;
    setActiveKey(key);
  }, []);

  // A page change underneath an open overlay swaps the FAQ pane; the thread is untouched.
  const previousPath = useRef(location.pathname);
  useEffect(() => {
    if (previousPath.current === location.pathname) return;
    previousPath.current = location.pathname;
    if (isOpenRef.current) setActive(resolved.context.key);
  }, [location.pathname, resolved.context.key, setActive]);

  useEffect(
    () => () => {
      if (replyTimer.current !== null) window.clearTimeout(replyTimer.current);
    },
    [],
  );

  const appendMessage = useCallback((input: MessageInput): HelpMessage => {
    const message: HelpMessage = { ...input, id: nextMessageId(), createdAt: Date.now() };
    setMessages((prev) => [...prev, message]);
    return message;
  }, []);

  const settleReply = useCallback(
    (produce: () => Promise<MessageInput>) => {
      pendingRef.current = true;
      setPending(true);
      const started = Date.now();

      const finish = (message: MessageInput) => {
        const dwell = dwellMs ?? dwellFor(message.text);
        const wait = Math.max(0, dwell - (Date.now() - started));
        replyTimer.current = window.setTimeout(() => {
          replyTimer.current = null;
          appendMessage(message);
          pendingRef.current = false;
          setPending(false);
        }, wait);
      };

      produce().then(finish, () =>
        finish({
          role: 'assistant',
          text: 'Something went wrong while looking that up. Try one of the questions on the left, or contact us below.',
          contextKey: activeKeyRef.current,
          status: 'error',
          variant: 'fallback',
        }),
      );
    },
    [appendMessage, dwellMs],
  );

  const openHelp = useCallback(
    (contextKey?: string) => {
      const key = contextKey && findContext(contextKey, contexts) ? contextKey : resolved.context.key;
      setActive(key);
      isOpenRef.current = true;
      setIsOpen(true);
    },
    [contexts, resolved.context.key, setActive],
  );

  const closeHelp = useCallback(() => {
    isOpenRef.current = false;
    setIsOpen(false);
  }, []);

  const setContext = useCallback(
    (contextKey: string) => {
      if (findContext(contextKey, contexts)) setActive(contextKey);
    },
    [contexts, setActive],
  );

  const askFaq = useCallback(
    (faqId: string) => {
      if (pendingRef.current) return;
      const faq = faqById.get(faqId);
      if (!faq) return;
      const contextKey = activeKeyRef.current;
      const context = findContext(contextKey, contexts) ?? globalContext(contexts);

      setSelectedFaqId(faq.id);
      appendMessage({ role: 'user', text: faq.question, contextKey, sourceFaqId: faq.id, status: 'ok' });
      settleReply(async () => ({
        role: 'assistant',
        text: await chatEngine.answerFaq(faq, context),
        contextKey,
        sourceFaqId: faq.id,
        status: 'ok',
        variant: 'answer',
      }));
    },
    [appendMessage, chatEngine, contexts, faqById, settleReply],
  );

  const sendMessage = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || pendingRef.current) return;
      const contextKey = activeKeyRef.current;
      const context = findContext(contextKey, contexts) ?? globalContext(contexts);

      appendMessage({ role: 'user', text: trimmed, contextKey, status: 'ok' });
      settleReply(async () => {
        const reply = await chatEngine.answerFreeText(trimmed, context, messages);
        if (reply.kind === 'faq') {
          setSelectedFaqId(reply.faq.id);
          const answer = await chatEngine.answerFaq(reply.faq, context);
          return {
            role: 'assistant',
            text: reply.preface ? `${reply.preface}\n\n${answer}` : answer,
            contextKey,
            sourceFaqId: reply.faq.id,
            status: 'ok',
            variant: 'answer',
          };
        }
        return {
          role: 'assistant',
          text: reply.text,
          contextKey,
          status: 'ok',
          variant: 'fallback',
          suggestedFaqIds: reply.suggestions.map((faq) => faq.id),
        };
      });
    },
    [appendMessage, chatEngine, contexts, messages, settleReply],
  );

  const runCta = useCallback(
    (cta: HelpFaqCta) => {
      if (cta.action === 'navigate') {
        const target = cta.target ? resolveCtaTarget(cta.target, resolved.params) : null;
        if (!target) return;
        closeHelp();
        if (target !== location.pathname) navigate(target);
        return;
      }
      if (cta.action === 'open_context') {
        if (cta.target) setContext(cta.target);
        return;
      }
      window.location.assign(buildHelpMailto({ email: contactEmail, contextLabel: activeContext.label, messages }));
    },
    [activeContext.label, closeHelp, contactEmail, location.pathname, messages, navigate, resolved.params, setContext],
  );

  const findFaq = useCallback((faqId: string) => faqById.get(faqId), [faqById]);

  const store = useMemo<HelpStore>(
    () => ({
      isOpen,
      activeContext,
      parentContext,
      routeParams: resolved.params,
      faqGroups,
      guide,
      messages,
      pending,
      selectedFaqId,
      draft,
      contactEmail,
      openHelp,
      closeHelp,
      setContext,
      askFaq,
      sendMessage,
      setDraft,
      runCta,
      findFaq,
    }),
    [
      isOpen, activeContext, parentContext, resolved.params, faqGroups, guide, messages, pending, selectedFaqId, draft,
      contactEmail, openHelp, closeHelp, setContext, askFaq, sendMessage, runCta, findFaq,
    ],
  );

  return (
    <HelpStoreContext.Provider value={store}>
      {children}
      {typeof document !== 'undefined' && createPortal(<HelpOverlay />, document.body)}
    </HelpStoreContext.Provider>
  );
}
